import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Input, Space, Switch, Tag, Typography, message } from "antd";
import { Link } from "react-router-dom";
import { fetchAgentSeoOverrides, saveAgentSeoOverride } from "../../../services/agentSeoOverridesApi";
import { fetchAgentSeoDashboardSummary } from "../../../services/agentSeoDashboardApi";

const { Text, Title } = Typography;
const { TextArea } = Input;

const TITLE_MAX = 70;
const DESCRIPTION_MAX = 160;

const ALLOWED_PATH_PATTERNS = [
  "/",
  "/blog/*",
  "/destinations/*",
  "/packages/*",
  "/hotels/*",
  "/vacation-packages/*",
  "/attractions/*",
  "/packages/profile/*",
  "/hotelprofile/*",
  "/tour/profile/*",
  "/contact",
  "/termsAndConditions",
  "/agentTerms",
];

const BLOCKED_PREFIXES = ["/search", "/p", "/agent", "/dashboard", "/checkout", "/payment", "/api"];

const toRuleRegex = (pattern) => {
  if (pattern === "/") {
    return /^\/$/;
  }
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -2).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`^${prefix}(?:/.*)?$`);
  }
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped}$`);
};

const ALLOWED_PATH_REGEX = ALLOWED_PATH_PATTERNS.map((pattern) => ({
  pattern,
  regex: toRuleRegex(pattern),
}));

const normalizePathname = (value) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }
  const withLeadingSlash = raw.startsWith("/") ? raw : `/${raw}`;
  const noQuery = withLeadingSlash.split("?")[0];
  return noQuery.replace(/\/+$/, "") || "/";
};

const isBlockedPath = (pathname) =>
  BLOCKED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const isAllowedPath = (pathname) => ALLOWED_PATH_REGEX.some((entry) => entry.regex.test(pathname));

const isValidInternalCanonical = (canonicalUrl) => {
  if (!canonicalUrl) {
    return true;
  }
  if (canonicalUrl.startsWith("/")) {
    return true;
  }
  try {
    const parsed = new URL(canonicalUrl);
    const allowedHosts = new Set(
      [window?.location?.hostname, "www.hotelsriver.com", "hotelsriver.com"].filter(Boolean).map((h) =>
        String(h).toLowerCase()
      )
    );
    return parsed.protocol.startsWith("http") && allowedHosts.has(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
};

const DEFAULT_FORM = {
  pathname: "",
  index_enabled: true,
  follow_enabled: true,
  title: "",
  description: "",
  canonical_url: "",
};

export default function AgentSeoSettingsPage() {
  const [overrides, setOverrides] = useState([]);
  const [summary, setSummary] = useState({
    total_articles: 0,
    draft_articles: 0,
    published_articles: 0,
    not_publish_ready_articles: 0,
    seo_override_pages: 0,
    seo_ai_daily_quota: 5,
    seo_ai_used_today: 0,
    seo_ai_remaining_today: 5,
  });
  const [formState, setFormState] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadSummary = async () => {
    try {
      const data = await fetchAgentSeoDashboardSummary();
      if (data && typeof data === "object") {
        setSummary({
          total_articles: Number(data.total_articles || 0),
          draft_articles: Number(data.draft_articles || 0),
          published_articles: Number(data.published_articles || 0),
          not_publish_ready_articles: Number(data.not_publish_ready_articles || 0),
          seo_override_pages: Number(data.seo_override_pages || 0),
          seo_ai_daily_quota: Number(data.seo_ai_daily_quota || 5),
          seo_ai_used_today: Number(data.seo_ai_used_today || 0),
          seo_ai_remaining_today: Number(data.seo_ai_remaining_today || 0),
        });
      }
    } catch {
      setSummary({
        total_articles: 0,
        draft_articles: 0,
        published_articles: 0,
        not_publish_ready_articles: 0,
        seo_override_pages: 0,
        seo_ai_daily_quota: 5,
        seo_ai_used_today: 0,
        seo_ai_remaining_today: 5,
      });
    }
  };

  const loadOverrides = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAgentSeoOverrides();
      setOverrides(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "טעינת SEO overrides נכשלה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverrides().catch(() => {});
    loadSummary().catch(() => {});
  }, []);

  const overridesByPath = useMemo(() => {
    return (overrides || []).reduce((acc, row) => {
      const normalized = normalizePathname(row?.pathname);
      if (normalized) {
        acc[normalized] = row;
      }
      return acc;
    }, {});
  }, [overrides]);

  const applyOverrideToForm = (pathname) => {
    const normalized = normalizePathname(pathname);
    const row = overridesByPath[normalized];
    if (!row) {
      setFormState((prev) => ({
        ...prev,
        pathname: normalized,
      }));
      return;
    }
    setFormState({
      pathname: normalized,
      index_enabled: typeof row.index_enabled === "boolean" ? row.index_enabled : true,
      follow_enabled: typeof row.follow_enabled === "boolean" ? row.follow_enabled : true,
      title: row.title || "",
      description: row.description || "",
      canonical_url: row.canonical_url || "",
    });
  };

  const validateForm = () => {
    const pathname = normalizePathname(formState.pathname);
    if (!pathname) {
      return "יש למלא pathname";
    }
    if (isBlockedPath(pathname)) {
      return "pathname חסום לעריכת סוכן";
    }
    if (!isAllowedPath(pathname)) {
      return "ניתן לערוך רק נתיבים ציבוריים מורשים";
    }
    if ((formState.title || "").length > TITLE_MAX) {
      return "title ארוך מדי (עד 70 תווים)";
    }
    if ((formState.description || "").length > DESCRIPTION_MAX) {
      return "description ארוך מדי (עד 160 תווים)";
    }
    const canonical = String(formState.canonical_url || "").trim();
    if (!isValidInternalCanonical(canonical)) {
      return "canonical_url חייב להיות יחסי או פנימי";
    }
    return null;
  };

  const handleSave = async () => {
    setSuccessMessage(null);
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const pathname = normalizePathname(formState.pathname);
      await saveAgentSeoOverride({
        pathname,
        index_enabled: Boolean(formState.index_enabled),
        follow_enabled: Boolean(formState.follow_enabled),
        title: String(formState.title || "").trim() || null,
        description: String(formState.description || "").trim() || null,
        canonical_url: String(formState.canonical_url || "").trim() || null,
      });
      setSuccessMessage("SEO override נשמר בהצלחה");
      message.success("SEO override נשמר בהצלחה");
      await loadOverrides();
      applyOverrideToForm(pathname);
    } catch (e) {
      const msg = e?.response?.data?.message || "שמירת SEO override נכשלה";
      setError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = useMemo(() => {
    const normalizedPath = normalizePathname(formState.pathname) || "/";
    const canonical = String(formState.canonical_url || "").trim();
    if (canonical.startsWith("/")) {
      return `https://www.hotelsriver.com${canonical}`;
    }
    if (canonical) {
      return canonical;
    }
    return `https://www.hotelsriver.com${normalizedPath}`;
  }, [formState.canonical_url, formState.pathname]);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="SEO Dashboard Metrics">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap size="middle">
            <Tag color="blue" data-testid="summary-total-articles">
              סה"כ מאמרים: {summary.total_articles}
            </Tag>
            <Tag color="orange" data-testid="summary-draft-articles">
              טיוטות: {summary.draft_articles}
            </Tag>
            <Tag color="green" data-testid="summary-published-articles">
              פורסמו: {summary.published_articles}
            </Tag>
            <Tag color="red" data-testid="summary-not-ready-articles">
              לא מוכנים לפרסום: {summary.not_publish_ready_articles}
            </Tag>
            <Tag color="purple" data-testid="summary-overrides">
              עמודים עם override: {summary.seo_override_pages}
            </Tag>
          </Space>
          <Card size="small" data-testid="summary-ai-quota-card">
            <Space direction="vertical" size={4}>
              <Text strong data-testid="summary-ai-today">
                טיוטות AI היום: {summary.seo_ai_used_today}/{summary.seo_ai_daily_quota}
              </Text>
              <Text data-testid="summary-ai-remaining">נותרו: {summary.seo_ai_remaining_today}</Text>
            </Space>
          </Card>

          <Space wrap>
            <Link to="/p/dashboard/seo">
              <Button data-testid="quick-link-manage-seo">ניהול SEO</Button>
            </Link>
            <Link to="/p/dashboard/seo/articles">
              <Button data-testid="quick-link-seo-articles">מאמרי SEO</Button>
            </Link>
            <Link to="/p/dashboard/seo/articles">
              <Button type="primary" data-testid="quick-link-create-draft">
                צור טיוטה חדשה
              </Button>
            </Link>
          </Space>
        </Space>
      </Card>

      <Card title="SEO Settings לסוכן" loading={loading}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text type="secondary">ניתן לערוך overrides רק עבור נתיבים ציבוריים שמוגדרים כ-allowAgentOverride.</Text>
          <div>
            <Text strong>נתיבים מותרים לעריכה:</Text>
            <div style={{ marginBlockStart: 8 }}>
              {ALLOWED_PATH_PATTERNS.map((pattern) => (
                <Tag key={pattern} style={{ marginBlockEnd: 8 }}>
                  {pattern}
                </Tag>
              ))}
            </div>
          </div>

          {error ? <Alert type="error" showIcon message={error} data-testid="seo-error" /> : null}
          {successMessage ? <Alert type="success" showIcon message={successMessage} data-testid="seo-success" /> : null}

          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text strong>Pathname</Text>
            <Input
              data-testid="pathname-input"
              placeholder="/blog/rhodes-guide"
              value={formState.pathname}
              onChange={(e) => setFormState((prev) => ({ ...prev, pathname: e.target.value }))}
              onBlur={(e) => applyOverrideToForm(e.target.value)}
            />
          </Space>

          <Space direction="horizontal" size="large">
            <Space>
              <Switch
                checked={Boolean(formState.index_enabled)}
                onChange={(checked) => setFormState((prev) => ({ ...prev, index_enabled: checked }))}
                data-testid="index-switch"
              />
              <Text>index_enabled</Text>
            </Space>
            <Space>
              <Switch
                checked={Boolean(formState.follow_enabled)}
                onChange={(checked) => setFormState((prev) => ({ ...prev, follow_enabled: checked }))}
                data-testid="follow-switch"
              />
              <Text>follow_enabled</Text>
            </Space>
          </Space>

          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <Text strong>Title</Text>
              <Text type={(formState.title || "").length > TITLE_MAX ? "danger" : "secondary"} data-testid="title-counter">
                {(formState.title || "").length}/{TITLE_MAX}
              </Text>
            </Space>
            <Input
              data-testid="title-input"
              value={formState.title}
              onChange={(e) => setFormState((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Rhodes Travel Guide"
            />
          </Space>

          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <Text strong>Description</Text>
              <Text
                type={(formState.description || "").length > DESCRIPTION_MAX ? "danger" : "secondary"}
                data-testid="description-counter"
              >
                {(formState.description || "").length}/{DESCRIPTION_MAX}
              </Text>
            </Space>
            <TextArea
              data-testid="description-input"
              value={formState.description}
              onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              placeholder="Best places to visit in Rhodes..."
            />
          </Space>

          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text strong>Canonical URL</Text>
            <Input
              data-testid="canonical-input"
              value={formState.canonical_url}
              onChange={(e) => setFormState((prev) => ({ ...prev, canonical_url: e.target.value }))}
              placeholder="/blog/rhodes-guide"
            />
          </Space>

          <Button type="primary" onClick={handleSave} loading={saving} data-testid="save-button">
            שמירה
          </Button>
        </Space>
      </Card>

      <Card title="Google Snippet Preview">
        <div data-testid="snippet-preview">
          <Title level={5} style={{ color: "#1a0dab", marginBottom: 4 }} data-testid="snippet-title">
            {formState.title || "כותרת לדוגמה לעמוד"}
          </Title>
          <Text style={{ color: "#0a7d35", display: "block" }} data-testid="snippet-url">
            {previewUrl}
          </Text>
          <Text type="secondary" data-testid="snippet-description">
            {formState.description || "תיאור העמוד יופיע כאן בהתאם לערך description שתגדירו."}
          </Text>
        </div>
      </Card>
    </Space>
  );
}
